import type { RFPContext } from "./types";

/**
 * Legal stub library (spec §11).
 *
 * MSA / DPA / GT&Cs are ALWAYS attach-stubs. Sensei never drafts the legal
 * documents themselves. Existing provisioned blocks are rendered verbatim so
 * the quality engine can prove no invented clauses were introduced.
 */

export interface LegalStub {
  key: string;
  title: string;
  body: string;
  version: string;
}

export function getLegalStubs(ctx: Pick<RFPContext, "legal_flags">): LegalStub[] {
  const stubs: LegalStub[] = [];

  stubs.push({
    key: "msa",
    title: "Master Services Agreement (attach)",
    body:
      "The awarded agreement will be the client's current MSA / GT&Cs, as attached at [ATTACH: Client MSA / GT&Cs]. No supplier (or Sensei-provisioned) terms apply unless expressly countersigned by the client. Any deviation requested by the bidder is listed in Appendix F (Contracting responses) and is not deemed accepted until written agreement.",
    version: "legal.v1",
  });

  if (ctx.legal_flags.personal_data === "yes") {
    stubs.push({
      key: "dpa",
      title: "Data Processing Agreement (attach)",
      body:
        "Where personal data is in scope, the client's standard DPA applies and is attached at [ATTACH: Client DPA]. The bidder is to confirm processing locations and sub-processor list in Appendix F. [GDPR note: applies in the stated geographies.]",
      version: "legal.v1",
    });
  }

  stubs.push({
    key: "slas",
    title: "Service Levels & Credits",
    body:
      "The service levels in Section 8 form the SLA schedule to the MSA. Breach credits operate as defined in the client's SLA schedule and are not capped by, or equivalent to, liquidated damages.",
    version: "legal.v1",
  });

  stubs.push({
    key: "amendment",
    title: "Change Control",
    body:
      "Changes to scope or rates are governed by the client's change control procedure (Appendix E, Change & Variation Register). No verbal instruction binds.",
    version: "legal.v1",
  });

  stubs.push({
    key: "exit",
    title: "Exit & Transition",
    body:
      "On termination the supplier will cooperate with the client's transition and data-recovery plan, providing data extracts in the agreed format at the agreed rates, for the defined period.",
    version: "legal.v1",
  });

  // Liability & insurance context routed from Section 1 by the Success-Measures
  // classifier. These are the buyer's stated liability / insurance positions
  // from the briefing; bidders must price and confirm coverage accordingly.
  if ((ctx.legal_flags.liability_notes?.length ?? 0) > 0) {
    stubs.push({
      key: "liability_context",
      title: "Liability & Insurance (briefing context)",
      body:
        "The briefing states the following liability / insurance positions. Bidders must confirm how their pricing and coverage respond to each, and flag any divergence in Appendix F (Contracting responses):\n\n" +
        ctx.legal_flags.liability_notes!.map((n) => `- ${n}`).join("\n"),
      version: "legal.v1",
    });
  }

  return stubs;
}